import { usePermissionStore } from '@core/stores/permissions.store';
import type { PermissionAction, PermissionGroup } from './actions';

/**
 * Reactive permission checks, for use during render.
 *
 * In Angular these were called directly from templates
 * (`*ngIf="permissionService.hasPermission('vendor:create')"`), which re-evaluated on every
 * change-detection cycle. As a Zustand selector the component re-renders only when that
 * specific permission changes.
 *
 * `undefined` is an accepted argument and yields `true` — "no requirement" rather than
 * "denied". That is what lets `<Can>` call both hooks unconditionally.
 */
export function usePermission(action: PermissionAction | undefined): boolean {
  return usePermissionStore((state) => (action ? !!state.actions?.[action] : true));
}

export function useModuleVisible(group: PermissionGroup | undefined): boolean {
  return usePermissionStore((state) =>
    group ? (state.groups?.[group]?.visible ?? false) : true,
  );
}

/** Somebody has delegated their approval authority to this user. See `PermissionsData.isDelegatee`. */
export function useIsDelegatee(): boolean {
  return usePermissionStore((state) => state.isDelegatee);
}

/**
 * `vendor:approve` OR a live delegation — the reactive twin of `canApproveWorkflows`.
 *
 * ⚠️ An ADDITION to who could approve before, never a replacement, and never a statement about a
 * particular record: `can_take_action` still decides that per row.
 */
export function useCanApproveWorkflows(): boolean {
  return usePermissionStore(
    (state) => !!state.actions?.['vendor:approve'] || state.isDelegatee,
  );
}

/** True once permissions have been loaded at least once (including from storage). */
export function usePermissionsHydrated(): boolean {
  return usePermissionStore((state) => state.loadedAt !== null);
}

/**
 * `hasSettingsAccess()` from `main-layout.component.ts` — the Settings nav item is visible when the
 * user can reach at least one sub-view under it.
 *
 * Angular checked only the two administrative modules (`user_management`, `role_management`).
 * `bulk_upload` was added after testing with a restricted account: `commercial@vendor.com` holds
 * `bulk:upload` and a visible `bulk_upload` group — the permission the backend issues specifically so a
 * user can reach Data Management — but neither admin module, so the entire Settings menu was hidden and
 * the one screen they were entitled to had no route into it. Angular had the same flaw, since Data
 * Management lived inside that same menu.
 *
 * Fixing only the route guard (see `router.tsx`) would have left an incoherent half-fix: the URL
 * permitted, the link absent.
 */
export function useSettingsAccess(): boolean {
  return usePermissionStore(
    (state) =>
      (state.groups?.['user_management']?.visible ?? false) ||
      (state.groups?.['role_management']?.visible ?? false) ||
      (state.groups?.['bulk_upload']?.visible ?? false),
  );
}
